/**
 * AnalyticsProvider — single source of truth for analytics context.
 *
 * What it does:
 *   1. Reads `VITE_PLAUSIBLE_DOMAIN` and (once, on mount) loads the
 *      proxied Plausible tracker. The loader is hostname-guarded, so
 *      preview deploys + localhost stay quiet.
 *   2. Exposes `useTrack()` — a typed, context-merging emitter.
 *
 * Why a context wrapper instead of a free function:
 *   - The `album` and `phase` are global properties we want on *every*
 *     event. Lifting them into context means call sites stay tight:
 *
 *       track('Album Switch', { from, to });
 *
 *     instead of every site importing the album state and the launch
 *     phase and threading them through.
 *   - One day soon there'll be a "logged in vs not" or "experiment
 *     bucket" dimension to add. New global prop = one line here, zero
 *     touches to call sites.
 *
 * SSR safety:
 *   - The provider renders children unconditionally during SSR.
 *   - The script-load effect only runs on the client.
 *   - `useTrack()` returns a function that no-ops outside a browser
 *     (typeof window === 'undefined') or when Plausible refused to load.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { loadPlausible, sendEvent } from './plausible';
import type { AnalyticsContext, EventMap, EventName, LaunchPhase } from './events';

interface AnalyticsProviderProps {
  album: string;
  phase: LaunchPhase;
  children: ReactNode;
}

const Ctx = createContext<AnalyticsContext | null>(null);

/**
 * Read the configured domain from Vite env. Returns '' when unset, which
 * makes `loadPlausible` skip injection entirely.
 */
function readDomain(): string {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_PLAUSIBLE_DOMAIN ?? '';
}

export function AnalyticsProvider({
  album,
  phase,
  children,
}: AnalyticsProviderProps): JSX.Element {
  // Mount-only; intentionally empty deps. The album/phase captured here are
  // the *initial-entry* values, which is exactly what we want frozen onto
  // the auto-pageview. Subsequent album swaps do not re-fire pageviews
  // (would inflate counts) — they fire `Album Switch` custom events whose
  // props are sourced from live context via useTrack.
  useEffect(() => {
    loadPlausible(readDomain(), { album, phase });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<AnalyticsContext>(() => ({ album, phase }), [album, phase]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Typed event emitter. Returns a stable callback whose identity changes
 * only when the underlying context does (album swap, phase flip), so
 * memoized children can depend on it without thrashing.
 *
 * Usage:
 *   const track = useTrack();
 *   track('Hero CTA Click', { kind: 'waitlist' });
 *
 * The compiler enforces matching prop shapes against EventMap; mis-spelled
 * event names won't autocomplete and won't typecheck.
 */
export function useTrack(): <K extends EventName>(
  name: K,
  props: EventMap[K]
) => void {
  const ctx = useContext(Ctx);

  return useCallback(
    <K extends EventName>(name: K, props: EventMap[K]) => {
      const merged: Record<string, unknown> = ctx
        ? { ...props, album: ctx.album, phase: ctx.phase }
        : { ...props };
      sendEvent(name, merged);
    },
    [ctx]
  );
}
